import { useEffect } from 'react';
import useSeo, { type SeoData } from '@/hooks/useSeo';

export default function ArtigoComoUsar() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const seoData: SeoData = {
    title: 'Como Usar CSSBuy Spreadsheet: Passo a Passo Completo 2026',
    description: 'Guia prático passo a passo para usar o CSSBuy Spreadsheet. Checklist de preparação, instruções detalhadas e dicas para maximizar seus resultados nas compras da China.',
    keywords: 'como usar CSSBuy Spreadsheet, tutorial CSSBuy, passo a passo CSSBuy, spreadsheet compras China, CSSBuy iniciante',
    canonicalPath: '/artigo/como-usar-cssbuy-spreadsheet',
    ogType: 'article',
    ogImage: 'https://readdy.ai/api/search-image?query=Clean%20organized%20desk%20with%20a%20notebook%20and%20smartphone%20displaying%20step%20by%20step%20checklist%20interface%2C%20warm%20natural%20morning%20light%2C%20minimalist%20workspace%20aesthetic%2C%20cream%20and%20beige%20tones%2C%20editorial%20photography%20with%20soft%20shadows&width=1200&height=630&seq=artigo-como-usar-hero-seo&orientation=landscape',
    schemaJsonLd: {
      '@type': 'BlogPosting',
      headline: 'Como Usar CSSBuy Spreadsheet: Passo a Passo Completo',
      description: 'Tutorial passo a passo completo de como usar o CSSBuy Spreadsheet para compras internacionais da China.',
      datePublished: '2026-06-02',
      dateModified: '2026-06-17',
      author: { '@type': 'Organization', name: 'CSSBuy Spreadsheet' },
      publisher: { '@type': 'Organization', name: 'CSSBuy Spreadsheet' },
      inLanguage: 'pt-BR',
      isAccessibleForFree: true,
    },
  };
  useSeo(seoData);

  return (
    <main className="min-h-screen bg-background-50">
      <article className="max-w-4xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <nav className="mb-8">
          <a
            href="/"
            className="inline-flex items-center gap-1.5 text-sm text-foreground-600 hover:text-primary-600 transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            Voltar para Home
          </a>
        </nav>

        <header className="mb-12">
          <span className="inline-block px-3 py-1 bg-primary-100 text-primary-700 rounded-full text-xs font-semibold tracking-wide mb-6">
            TUTORIAL
          </span>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground-950 leading-tight mb-6">
            Como Usar CSSBuy Spreadsheet: Passo a Passo Completo
          </h1>
          <p className="text-lg text-foreground-600 leading-relaxed max-w-2xl">
            Um guia prático com instruções detalhadas para configurar e utilizar o
            CSSBuy Spreadsheet em todas as etapas da sua importação.
          </p>
          <div className="flex items-center gap-4 mt-6 text-sm text-foreground-500">
            <span>Atualizado: Junho 2026</span>
            <span className="w-1 h-1 rounded-full bg-foreground-400"></span>
            <span>Leitura: 10 min</span>
          </div>
        </header>

        <div className="w-full h-[320px] md:h-[420px] rounded-2xl overflow-hidden mb-14">
          <img
            src="https://readdy.ai/api/search-image?query=Clean%20organized%20desk%20with%20a%20notebook%20and%20smartphone%20displaying%20step%20by%20step%20checklist%20interface%2C%20warm%20natural%20morning%20light%2C%20minimalist%20workspace%20aesthetic%2C%20cream%20and%20beige%20tones%2C%20editorial%20photography%20with%20soft%20shadows&width=1200&height=630&seq=artigo-como-usar-hero-v2&orientation=landscape"
            alt="Como Usar CSSBuy Spreadsheet"
            className="w-full h-full object-cover object-top"
          />
        </div>

        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
            Preparação Inicial
          </h2>
          <p className="text-foreground-700 leading-relaxed mb-4">
            Antes de começar a usar o <strong>CSSBuy Spreadsheet</strong>, você
            precisa ter uma conta ativa na plataforma CSSBuy. O processo de registro
            é simples e gratuito — basta acessar o site principal e preencher seus
            dados básicos.
          </p>
          <p className="text-foreground-700 leading-relaxed mb-4">
            Após criar sua conta, você terá acesso ao painel principal onde poderá
            copiar links de produtos, gerenciar seus pedidos e utilizar o spreadsheet
            para organizar suas compras internacionais.
          </p>
          <div className="bg-background-100 rounded-xl p-5 border border-background-200/60 mt-6">
            <h3 className="font-semibold text-foreground-900 mb-3">
              Checklist antes de começar:
            </h3>
            <ul className="space-y-2 text-sm text-foreground-700">
              <li className="flex gap-2">
                <i className="ri-checkbox-circle-line text-secondary-500"></i>
                Conta CSSBuy criada e verificada
              </li>
              <li className="flex gap-2">
                <i className="ri-checkbox-circle-line text-secondary-500"></i>
                Método de pagamento configurado
              </li>
              <li className="flex gap-2">
                <i className="ri-checkbox-circle-line text-secondary-500"></i>
                Lista de produtos desejados em mãos
              </li>
              <li className="flex gap-2">
                <i className="ri-checkbox-circle-line text-secondary-500"></i>
                Orçamento definido para a compra
              </li>
            </ul>
          </div>
        </section>

        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
            Passo a Passo Detalhado
          </h2>

          <div className="space-y-4">
            {[
              {
                step: '1',
                title: 'Acesse o site principal do CSSBuy',
                desc: 'Entre na plataforma usando seu login e senha. No painel principal, você encontrará todas as ferramentas necessárias para começar suas compras.',
              },
              {
                step: '2',
                title: 'Copie o link do produto desejado',
                desc: 'Navegue pelas lojas parceiras e encontre os produtos que deseja importar. Copie o URL completo de cada produto que pretende comprar.',
              },
              {
                step: '3',
                title: 'Insira os dados no spreadsheet',
                desc: 'Cole o link do produto na planilha e preencha as colunas com informações relevantes: nome do produto, preço em yuan, peso estimado e loja de origem.',
              },
              {
                step: '4',
                title: 'Compare as opções disponíveis',
                desc: 'Com vários produtos registrados, compare preços, qualidade e prazos de entrega para fazer a melhor escolha.',
              },
              {
                step: '5',
                title: 'Finalize o pedido e acompanhe',
                desc: 'Após escolher os melhores produtos, faça o pedido e utilize o spreadsheet para acompanhar o status de cada item até a entrega.',
              },
            ].map((item, i) => (
              <div
                key={i}
                className="flex gap-4 p-4 rounded-xl bg-background-100 border border-background-200/60"
              >
                <div className="w-10 h-10 flex-shrink-0 rounded-full bg-primary-500 text-background-50 flex items-center justify-center font-bold text-sm">
                  {item.step}
                </div>
                <div>
                  <h3 className="font-semibold text-foreground-900 mb-1">
                    {item.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-14">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
            Dicas para Maximizar Resultados
          </h2>
          <ul className="space-y-3">
            {[
              'Mantenha o spreadsheet sempre atualizado com o status de cada pedido',
              'Use cores diferentes para destacar produtos prioritários',
              'Registre o histórico de preços para identificar promoções',
              'Anote prazos de entrega estimados para cada método de frete',
              'Compare pelo menos 3 fornecedores antes de decidir',
            ].map((tip, i) => (
              <li key={i} className="flex gap-2 text-sm text-foreground-700">
                <span className="text-primary-500 mt-0.5">▸</span>
                {tip}
              </li>
            ))}
          </ul>
        </section>

        <section className="bg-primary-500 rounded-2xl p-8 md:p-10 text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-4">
            Comece a Usar Agora
          </h2>
          <p className="text-background-50/85 mb-6 max-w-md mx-auto leading-relaxed">
            Coloque em prática tudo que aprendeu e comece a importar da China com
            organização e segurança.
          </p>
          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-background-50 text-primary-600 rounded-full text-base font-semibold hover:bg-background-100 transition-all cursor-pointer whitespace-nowrap"
          >
            CSSBuy Spreadsheet
            <i className="ri-arrow-right-up-line text-lg"></i>
          </a>
        </section>

        <section className="mt-12 pt-10 border-t border-background-200/70">
          <h3 className="text-lg font-semibold text-foreground-900 mb-4">
            Leia também
          </h3>
          <div className="flex flex-wrap gap-3">
            <a
              href="/artigo/cssbuy-spreadsheet-guia-completo"
              className="px-4 py-2 bg-background-100 hover:bg-background-200 rounded-full text-sm text-foreground-700 hover:text-foreground-900 transition-colors"
            >
              Guia Completo CSSBuy
            </a>
            <a
              href="/artigo/comparacao-cssbuy-pandabuy-superbuy"
              className="px-4 py-2 bg-background-100 hover:bg-background-200 rounded-full text-sm text-foreground-700 hover:text-foreground-900 transition-colors"
            >
              Comparação de Agentes
            </a>
            <a
              href="/"
              className="px-4 py-2 bg-primary-100 hover:bg-primary-200 rounded-full text-sm text-primary-700 transition-colors font-medium"
            >
              ← Voltar para Home
            </a>
          </div>
        </section>
      </article>
    </main>
  );
}